import {useEffect, useMemo} from 'react'
import {useServices} from '@/services/context'
import {useQuery} from '@tanstack/react-query'
import {useTranslation} from 'react-i18next'
import type {TypeValue} from '@/types/types'
import {bridgeAppConfigToI18next} from '@/i18n/app-config-bridge'
import {
    EMPTY_CONFIG,
    DEFAULT_PAGINATION_CONFIG,
    resolveLabel,
    localizeTypeValues,
    labelOf,
    codesOf,
} from './app-config-helpers'
import type {AppConfig, LocalizedTypeValue, PaginationConfig} from './app-config-helpers'

export type {AppConfig, LocalizedTypeValue, PaginationConfig}
export {
    EMPTY_CONFIG,
    DEFAULT_PAGINATION_CONFIG,
    resolveLabel,
    localizeTypeValues,
    labelOf,
    codesOf,
}

const APP_CONFIG_QUERY_KEY = ['app-config'] as const

const TYPE_VALUE_KEYS = [
    'accessProfileBindingOperationTypes',
    'accessProfileStatuses',
    'accountStatuses',
    'assignmentStatuses',
    'connectorTypes',
    'dataClassificationTypes',
    'domainEntityTypes',
    'enforcementTypes',
    'environmentTypes',
    'functionGroupTypes',
    'identifierTypes',
    'identityTypes',
    'lifecycleStates',
    'partyRelationshipTypes',
    'partyTypes',
    'resourceResponsibilityTypes',
    'resourceTypes',
    'riskLevelTypes',
    'systemTypes',
    'targetSystemResponsibilityTypes',
    'accessProfileResponsibilityTypes',
    'functionGroupResponsibilityTypes',
    'restrictionRuleResponsibilityTypes',
    'autoAssignRuleResponsibilityTypes',
    'certificationPolicyResponsibilityTypes',
    'certificationCampaignResponsibilityTypes',
    'workflowSteps',
] as const

export function useAppConfig(): AppConfig & {isLoading: boolean} {
    const services = useServices()
    const {data, isLoading} = useQuery({
        queryKey: APP_CONFIG_QUERY_KEY,
        queryFn: () => services.config.get() as Promise<AppConfig>,
        // WHY: App config loads once per session — every page that calls useAppConfig()
        // would otherwise refire /api/i/config on mount under the global staleTime: 0.
        // Admin mutations to type values / attribute definitions invalidate this key
        // explicitly (queryClient.invalidateQueries({queryKey: ['app-config']})); the
        // 5-minute window is a belt-and-braces fallback for any path that mutates
        // type-shape data without invalidating. Issue 388.
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    })
    const {i18n} = useTranslation()
    const lang = i18n.language

    useEffect(() => {
        if (data) bridgeAppConfigToI18next(data)
    }, [data])

    const localized = useMemo(() => {
        if (!data) return EMPTY_CONFIG
        const result = {
            ...EMPTY_CONFIG,
            ...data,
            supportedLocales: data.supportedLocales ?? EMPTY_CONFIG.supportedLocales,
            defaultLocale: data.defaultLocale ?? EMPTY_CONFIG.defaultLocale,
        }
        for (const key of TYPE_VALUE_KEYS) {
            const raw = data[key] as TypeValue[] | undefined
            if (raw) {
                (result as Record<string, unknown>)[key] = localizeTypeValues(raw, lang)
            }
        }
        if (data.typeValuesByName) {
            const localized: Record<string, LocalizedTypeValue[]> = {}
            for (const [name, values] of Object.entries(data.typeValuesByName)) {
                localized[name] = localizeTypeValues(values as TypeValue[], lang)
            }
            result.typeValuesByName = localized
        }
        return result
    }, [data, lang])

    return {
        ...localized,
        isLoading,
    }
}
